import React, {useState, useEffect, useCallback, useRef} from 'react';
import {Button, DateInput, SelectInput, Text, TextInput} from '@/ui-kits';
import {useSelector, useDispatch} from 'react-redux';
import {
  patientAdminDashboardConsultationsSelector,
  patientAdminDashboardCurrentPageSelector,
  patientAdminDashboardTotalPagesSelector,
  patientAdminDashboardTotalResultsSelector,
  patientAdminStatus,
  patientAdminFailureSelector,
} from '@/services/redux/selectors';
import {getDashboardConsultationsRequest, resetPatientAdminMessage} from '@/services/redux/slices';
import {ErrorCodes, getErrorMessage} from '@/utils/getMessages';
import {useToast, useClickOutside} from '@/hooks';
import {ConsultationStatus, ConsultationType, ConsultationStatusLabels, ConsultationTypeLabels} from '@/constants';
import {defaultDeadlineHours} from '@/constants/defaults';
import {DataTable} from '@/components';
import {DataTableColumn, AdminDashboardConsultation, GetPatientAdminDashboardConsultationsRequestPayload, SelectInputitem} from '@/interface';
import {enumToSelectOptions, formatName, formatDate, calculateTATHours} from '@/utils/common_functions';

const StatusFilter: SelectInputitem[] = [
  { id: ConsultationStatus.AwaitingDoctorReview, label: 'Awaiting doctor review'},
  { id: ConsultationStatus.UnderReviewByDoctor, label: 'Under doctor review'},
  { id: ConsultationStatus.ReportReady, label: 'Report Ready'},
  { id: ConsultationStatus.Completed, label: 'Completed'},
  { id: ConsultationStatus.CancelledByPatient, label: 'Cancelled'},
  { id: ConsultationStatus.SpecialistAllocationPending, label: 'Awaiting Doctor Assignment'},
]
const TypeFilterOptions = enumToSelectOptions(ConsultationType)

const columns: DataTableColumn<AdminDashboardConsultation>[] = [
  {
    name: 'case id',
    align: 'center',
    render: row => row.case_id,
    title: <Text>CASE ID</Text>,
    width: {minWidth: '100px'},
  },
  {
    name: 'patient id',
    align: 'center',
    render: row => row.patient_id,
    title: <Text>PATIENT ID</Text>,
    width: {minWidth: '150px'},
  },
  {
    name: 'patient name',
    align: 'left',
    render: row => formatName(row.patient_name),
    title: <Text>PATIENT NAME</Text>,
    width: {minWidth: '170px'},
  },
  {
    name: 'specialist',
    align: 'left',
    render: row => row.specialist_name ? 'Dr. ' + formatName(row.specialist_name) : 'N/A',
    title: <Text>SPECIALIST</Text>,
    width: {minWidth: '200px'},
  },
  {
    name: 'Specialization',
    align: 'left',
    render: row => row.category.label,
    title: <Text>SPECIALIZATION</Text>,
    width: {minWidth: '200px'},
  },
  {
    name: 'type',
    align: 'left',
    render: row => ConsultationTypeLabels[row.type.id],
    title: <Text>CONSULTATION TYPE</Text>,
    width: {minWidth: '230px'},
  },
  {
    name: 'created at',
    align: 'center',
    render: row => formatDate(row.created_at, 'yyyy-MMM-dd, HH:mm:ss'),
    title: <Text>Booked date & time</Text>,
    width: {minWidth: '230px'},
  },
  {
    name: 'status',
    align: 'left',
    render: row => ConsultationStatusLabels[row.status.id],
    title: <Text>STATUS</Text>,
    width: {minWidth: '100px'},
  },
  {
    name: 'turnaround time',
    align: 'center',
    render: row => {
      if (row.status.id === ConsultationStatus.ReportReady || row.status.id === ConsultationStatus.Completed) {
        if (!row.close_at) return '--';
        const tat = calculateTATHours(row.created_at, row.close_at);
        const hours = Math.floor((new Date(row.close_at).getTime() - new Date(row.created_at).getTime()) / (1000 * 60 * 60));
        const isOverDeadline = hours > defaultDeadlineHours;
        return (
          <span>
            {formatDate(row.close_at, 'dd-MMM-yyyy, HH:mm:ss')}{' '}
            <span className={isOverDeadline ? 'text-ui_red' : ''}>({tat})</span>
          </span>
        );
      }
      return '--';
    },
    title: <Text>TURNAROUND TIME (TAT)</Text>,
    width: {minWidth: '250px'},
  },
];

export const PatientAdminDashboard: React.FC = () => {
  // ==========================
  // hooks
  // ==========================
  const {showToast} = useToast();
  const dispatch = useDispatch();

  // ==========================
  // selectors
  // ==========================
  const consultations = useSelector(patientAdminDashboardConsultationsSelector);
  const consultationsCurrentPage = useSelector(patientAdminDashboardCurrentPageSelector);
  const consultationsTotalPages = useSelector(patientAdminDashboardTotalPagesSelector);
  const consultationsTotalResults = useSelector(patientAdminDashboardTotalResultsSelector);
  const consultationsFailure = useSelector(patientAdminFailureSelector) as ErrorCodes;
  const isLoading = useSelector(patientAdminStatus);

  // ===========================
  // states
  // ===========================
  const [search, setSearch] = useState<string>('');
  const [status, setStatus] = useState<ConsultationStatus | null>(null);
  const [type, setType] = useState<ConsultationType | null>(null);
  const [page, setPage] = useState(1);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useClickOutside<HTMLButtonElement>(() => setIsDatePickerOpen(false));
  const dateDropdownRef = useRef<HTMLDivElement | null>(null);

  // ===========================
  // functions
  // ===========================
  const resetFilter = () => {
    setSearch('');
    setStatus(null);
    setType(null);
    setPage(1);
    setStartDate(null);
    setEndDate(null);
  };

  const fetchConsultations = useCallback(() => {
    let isFilterActive = false;
    const payload: GetPatientAdminDashboardConsultationsRequestPayload = {
      page,
    };
    if (search) {
      payload.search_text = search;
      isFilterActive = true;
    }
    if (status) {
      payload.consultation_status = status;
      isFilterActive = true;
    }
    if (type) {
      payload.consultation_type = type;
      isFilterActive = true;
    }
    if (startDate) {
      payload.start_date = startDate.toISOString();
      isFilterActive = true;
    }
    if (endDate) {
      payload.end_date = endDate.toISOString();
      isFilterActive = true;
    }
    setIsFilterActive(isFilterActive);
    dispatch(getDashboardConsultationsRequest(payload));
  }, [page, search, status, type, startDate, endDate]);

  // ===========================
  // side effects
  // ===========================
  useEffect(() => {
    const timeout = setTimeout(fetchConsultations, 500);
    return () => clearTimeout(timeout);
  }, [fetchConsultations]);

  useEffect(() => {
    if (consultationsFailure) {
      if (['E-10044', 'E-10054'].includes(consultationsFailure)) {
        setErrorMessage(getErrorMessage(consultationsFailure));
        return;
      }
      showToast(getErrorMessage(consultationsFailure), 'failure');
    }
    return () => {
      dispatch(resetPatientAdminMessage());
    };
  }, [consultationsFailure]);

  useEffect(() => {
    if (isDatePickerOpen && dateDropdownRef.current) {
      const el = dateDropdownRef.current;
      const rect = el.getBoundingClientRect();

      // If dropdown is overflowing on the right
      if (rect.right > window.innerWidth) {
        el.style.right = 'auto';
        el.style.left = '0';
      }

      // If dropdown is overflowing on the left
      if (rect.left < 0) {
        el.style.left = '0';
      }
    }
  }, [isDatePickerOpen]);

  return (
    <div className="p-2 sm:py-4: md:p-6 flex flex-col grow gap-4">
      <Text variant="heading" className="text-ui_blue mb-4">
        Patient Consultation
      </Text>

      <div className="grid grid-cols-2 sm:flex flex-wrap gap-2 z-[100]">
        <TextInput
          value={search}
          onChangeText={setSearch}
          leftIcon="search_blue"
          className="sm:max-w-[300px] w-full col-span-full"
          wrapperClassName="px-2 border"
          placeholder="Search by Name, ID & case ID"
        />
        <SelectInput
          value={status}
          onChange={value => setStatus(value.id)}
          className="sm:max-w-[200px] w-full"
          itemTextClassName="text-wrap !text-sm"
          placeholder="Status"
          wrapperClassName="border"
          options={StatusFilter}
        />
        <SelectInput
          value={type}
          onChange={value => setType(value.id)}
          className="sm:max-w-[200px] w-full"
          placeholder="Consultation Type"
          wrapperClassName="border"
          itemTextClassName='!text-sm'
          options={TypeFilterOptions}
        />

        <DateInput
          value={startDate}
          onChangeDate={setStartDate}
          placeholder="From"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          className="sm:max-w-[150px] w-full sm:w-fit min-w-[100px]"
          textClassName="text-start text-ui_blue"
          maxValue={endDate ?? new Date()}
        />
        <DateInput
          value={endDate}
          onChangeDate={setEndDate}
          placeholder="To"
          labelClassName="!font-FigtreeSemiBold"
          wrapperClassName="border rounded-lg"
          textClassName="text-start text-ui_blue"
          className="sm:max-w-[150px] w-full sm:w-fit min-w-[100px]"
          minValue={startDate ?? undefined}
          maxValue={new Date()}
        />

        {isFilterActive && <Button title="Reset Filters" variant="secondary" onClick={resetFilter} />}
      </div>

      <DataTable
        columns={columns}
        data={consultations}
        totalPages={consultationsTotalPages}
        currentPage={consultationsCurrentPage}
        totalResult={consultationsTotalResults}
        onPageChange={setPage}
        loading={isLoading}
        pageSize={50}
        errorMessage={errorMessage}
      />
    </div>
  );
};
